import { CommonModule, DatePipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { AuditEvent } from 'app/model/audit/audit-history.model';
import { AuditHistoryQueryParams, AuditHistoryService } from 'app/services/audit-history.service';

export interface AuditHistoryDialogData {
    entityType: string;
    entityId?: string;
    scope?: string;
    title: string;
}

@Component({
    selector: 'app-audit-history-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        DatePipe,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatPaginatorModule
    ],
    templateUrl: './audit-history-dialog.component.html',
    styleUrls: ['./audit-history-dialog.component.scss']
})
export class AuditHistoryDialogComponent implements OnInit {
    loading = true;
    errorMessage: string | null = null;
    events: AuditEvent[] = [];
    dialogTitle = '';
    pageIndex = 0;
    pageSize = 25;
    totalElements = 0;
    readonly pageSizeOptions = [10, 25, 50, 100];
    actionFilter: '' | 'CREATE' | 'UPDATE' | 'DELETE' = '';
    dateFrom = '';
    dateTo = '';
    private readonly fieldLabels: Record<string, string> = {
        id: 'Identifiant',
        patient: 'Patient',
        patientId: 'Patient',
        patientCode: 'Code patient',
        doctor: 'Medecin',
        consultationDate: 'Date de consultation',
        reason: 'Motif',
        medicalHistory: 'Histoire de la maladie',
        clinicalExam: 'Examen clinique',
        complementaryExam: 'Examen complementaire',
        diagnosis: 'Diagnostic',
        treatment: 'Traitement',
        notes: 'Notes',
        nextAppointment: 'Prochain rendez-vous',
        createdAt: 'Date de creation',
        updatedAt: 'Date de modification',
        status: 'Statut',
        admissionDate: 'Date d admission',
        dischargeDate: 'Date de sortie',
        requestDate: 'Date de demande',
        requestCode: 'Code demande',
        startDate: 'Debut',
        endDate: 'Fin',
        code: 'Code',
        name: 'Nom',
        label: 'Libelle',
        title: 'Titre',
        entryCode: 'Code admission',
        entryDate: 'Date d enregistrement',
        medicalServiceLine: 'Ligne d admission',
        medicalServiceChoice: 'Service medical',
        admissionMotif: 'Motif d admission',
        analysisRequest: 'Demande d analyse',
        labParameter: 'Parametre',
        resultValue: 'Resultat',
        remarks: 'Remarque',
        enteredBy: 'Saisi par',
        medicalAct: 'Acte medical',
        serviceCatalog: 'Service',
        product: 'Produit',
        drugLabel: 'Medicament',
        drugCodeCIP: 'Code CIP',
        quantity: 'Quantite',
        price: 'Prix',
        customValue: 'Valeur personnalisee',
        performedAt: 'Date de realisation',
        comments: 'Commentaire',
        observation: 'Observation',
        statusFacture: 'Statut facture',
        invoice: 'Facture',
        invoiceNumber: 'Numero de facture',
        stockType: 'Type de stock',
        objectName: 'Objet',
        batchNumber: 'Numero de lot',
        source: 'Source',
        destination: 'Destination',
        expirationDate: 'Date expiration',
        movementReason: 'Raison du mouvement',
        movementReference: 'Référence inventaire',
        movementComment: 'Commentaire inventaire',
        inventoryGrandTheoretical: 'Grand stock théorique',
        inventoryGrandCounted: 'Grand stock compté',
        inventorySecondaryTheoretical: 'Stock secondaire théorique',
        inventorySecondaryCounted: 'Stock secondaire compté',
        inventoryGrandDelta: 'Écart grand stock',
        inventorySecondaryDelta: 'Écart stock secondaire',
        inventoryReconciledQuantity: 'Quantité réconciliée',
        insurances: 'Assurances',
        mutual: 'Assurance',
        insuranceNumber: 'Numero d assure',
        isPrimary: 'Assurance principale',
        isActive: 'Assurance active',
        coveragePercentage: 'Pourcentage de couverture',
        email: 'Email',
        username: 'Nom utilisateur',
        role: 'Role',
        phone: 'Telephone',
        firstName: 'Prenom',
        lastName: 'Nom'
    };
    private readonly technicalFields = new Set([
        'id',
        'createdAt',
        'updatedAt',
        'revision',
        'revisionType',
        'entityNames',
        'timestamp'
    ]);

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: AuditHistoryDialogData,
        private auditHistoryService: AuditHistoryService
    ) {
        this.dialogTitle = data.title;
    }

    ngOnInit(): void {
        this.loadHistory();
    }

    loadHistory(resetPage: boolean = false): void {
        if (resetPage) {
            this.pageIndex = 0;
        }
        this.loading = true;
        this.errorMessage = null;

        const request$ = this.resolveRequest();
        request$.subscribe({
            next: (response) => {
                this.events = response.events || [];
                this.totalElements = response.totalElements ?? this.events.length;
                this.pageIndex = response.page ?? this.pageIndex;
                this.pageSize = response.size ?? this.pageSize;
                this.dialogTitle = this.computeDialogTitle();
                this.loading = false;
            },
            error: (error) => {
                console.error('Erreur lors du chargement de l\'historique', error);
                this.errorMessage = 'Impossible de charger l\'historique pour cette page.';
                this.loading = false;
            }
        });
    }

    onPageChange(event: PageEvent): void {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
        this.loadHistory();
    }

    clearFilters(): void {
        this.actionFilter = '';
        this.dateFrom = '';
        this.dateTo = '';
        this.loadHistory(true);
    }

    private buildQuery(): AuditHistoryQueryParams {
        return {
            page: this.pageIndex,
            size: this.pageSize,
            action: this.actionFilter,
            dateFrom: this.dateFrom || undefined,
            dateTo: this.dateTo || undefined,
            scope: this.data.scope
        };
    }

    private resolveRequest() {
        const query = this.buildQuery();

        if (this.data.entityType === 'PatientTimeline' && this.data.entityId) {
            return this.auditHistoryService.getPatientTimeline(this.data.entityId, query);
        }

        if (this.data.entityType === 'EntryTimeline' && this.data.entityId) {
            return this.auditHistoryService.getEntryTimeline(this.data.entityId, query);
        }

        if (this.data.entityType === 'AnalysisTimeline') {
            return this.auditHistoryService.getAnalysisTimeline(query);
        }

        if (this.data.entityType === 'AnalysisResultsTimeline' && this.data.entityId) {
            return this.auditHistoryService.getAnalysisResultsTimeline(this.data.entityId, query);
        }

        if (this.data.entityType === 'HospitalizationTimeline' && this.data.entityId) {
            return this.auditHistoryService.getHospitalizationTimeline(this.data.entityId, query);
        }

        if (this.data.entityType === 'StockTimeline') {
            return this.auditHistoryService.getStockTimeline(query);
        }

        if (this.data.entityId) {
            return this.auditHistoryService.getEntityHistory(this.data.entityType, this.data.entityId, query);
        }

        return this.auditHistoryService.getGlobalEntityHistory(this.data.entityType, query);
    }

    actionLabel(action: AuditEvent['action']): string {
        if (action === 'CREATE') return 'Création';
        if (action === 'DELETE') return 'Suppression';
        return 'Modification';
    }

    fieldLabel(field: string): string {
        return this.fieldLabels[field] || this.humanizeField(field);
    }

    entityDisplayLabel(event: AuditEvent): string {
        const label = event.entityLabel?.trim();
        if (label) {
            return label;
        }
        return this.humanizeField(event.entityType || 'enregistrement');
    }

    shouldDisplayChange(field: string): boolean {
        const normalized = field?.trim().toLowerCase();
        if (!normalized) {
            return false;
        }

        if (this.technicalFields.has(field)) {
            return false;
        }

        if (normalized === 'id' || normalized.endsWith('id')) {
            return false;
        }

        if (this.data.entityId) {
            return true;
        }

        return !this.technicalFields.has(field);
    }

    formatValue(value: string | null): string {
        if (value === null) return 'null';
        if (value === 'true') return 'Oui';
        if (value === 'false') return 'Non';
        if (value === 'WAITING') return 'En attente';
        if (value === 'SERVING') return 'En cours';
        if (value === 'SERVED') return 'Accepte';
        if (value === 'CANCELLED') return 'Refuse';
        const asDate = this.tryFormatDate(value);
        if (asDate) return asDate;
        return value;
    }

    private humanizeField(field: string): string {
        return field
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/_/g, ' ')
            .replace(/^./, (s) => s.toUpperCase());
    }

    private tryFormatDate(raw: string): string | null {
        const value = raw.trim();
        if (!value) return null;

        const isoDateTime = /^\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}/.test(value);
        const isoDate = /^\\d{4}-\\d{2}-\\d{2}$/.test(value);
        if (!isoDateTime && !isoDate) return null;

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hour = String(date.getHours()).padStart(2, '0');
        const minute = String(date.getMinutes()).padStart(2, '0');
        const second = String(date.getSeconds()).padStart(2, '0');

        if (isoDate) {
            return `${day}/${month}/${year}`;
        }

        return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
    }

    trackByRevision(_index: number, event: AuditEvent): string {
        return `${event.revision}-${event.entityId}`;
    }

    private computeDialogTitle(): string {
        if (this.data.entityType === 'PatientTimeline') {
            return this.data.title;
        }

        if (!this.data.entityId) {
            return this.data.title;
        }

        if (this.data.entityType === 'EntryTimeline') {
            const code = this.findEntityLabel(['Entry']);
            return `Historique complet de l'admission ${code || this.data.entityId}`;
        }

        if (this.data.entityType === 'HospitalizationTimeline') {
            const code = this.findEntityLabel(['Hospitalization']);
            return `Historique complet de l'hospitalisation ${code || this.data.entityId}`;
        }

        if (this.data.entityType === 'AnalysisResultsTimeline') {
            const code = this.findEntityLabel(['AnalysisRequest']);
            return `Historique complet des resultats d'analyse ${code || this.data.entityId}`;
        }

        const label = this.findEntityLabel([this.data.entityType]);
        if (label) {
            return `Historique complet de ${label}`;
        }
        return `${this.data.title} ${this.data.entityId}`;
    }

    private findEntityLabel(entityTypes: string[]): string | null {
        const normalizedTypes = new Set(entityTypes.map((type) => this.normalizeType(type)));
        for (const event of this.events) {
            if (!normalizedTypes.has(this.normalizeType(event.entityType))) {
                continue;
            }
            const label = event.entityLabel?.trim();
            if (label) {
                return label;
            }
        }

        for (const event of this.events) {
            const label = event.entityLabel?.trim();
            if (label) {
                return label;
            }
        }

        return null;
    }

    private normalizeType(value: string | null | undefined): string {
        return (value || '').toLowerCase().replace(/[_\s-]/g, '');
    }
}
